import {
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useNavigate,
  useParams,
} from "react-router";
import { core, coreConfig } from "../../..";
import * as Sentry from "@sentry/react";
import semver from "semver";
import { VERSION } from "../../../config/version";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Layout, List, Tag, Typography } from "antd";
import { AppSpecifier } from "@data-root/types";

const { Content } = Layout;

export const appConfigurationLoader = async (args: LoaderFunctionArgs) => {
  console.log("appConfigurationLoader");

  const { appId, deploymentId } = args.params;

  await core.recover(coreConfig);
  if (!core.user) {
    const url = new URL(args.request.url);
    const next = url.pathname;
    const redirectUrl = `/auth?next=${encodeURIComponent(next)}`;
    throw redirect(redirectUrl);
  }

  Sentry.setUser({
    id: core.user!.id,
    username: core.user!.name,
    email: core.user!.email,
  });

  const appSpecifier: AppSpecifier = {
    appId,
    deploymentId,
  };

  await core.loadApp(appSpecifier);
  const { app } = await core.getApp(appSpecifier);

  if (app.dependencies && "eigen" in app.dependencies)
    if (!semver.satisfies(VERSION, app.dependencies.eigen))
      throw new Error(
        `This app requires Eigen version ${app.dependencies.eigen}. You are using version ${VERSION}.`
      );

  return { app };
};

export const AppPermissionsScreen = () => {
  const { appId, environmentId } = useParams();
  const navigate = useNavigate();

  const { app } = useLoaderData() as Awaited<
    ReturnType<typeof appConfigurationLoader>
  >;

  const permissions = useMemo(() => {
    if (!app.configuration?.permissions.length) return [];

    return app.configuration.permissions;
  }, [app.configuration.permissions]);

  const {
    data: statuses,
    isSuccess,
    isLoading,
  } = useQuery({
    queryKey: ["permissions", permissions],
    queryFn: async () => {
      const statuses = await Promise.all(
        permissions.map((permission) =>
          navigator.permissions.query({ name: permission })
        )
      );
      return statuses;
    },
  });

  const onReload = useCallback(() => {
    location.reload();
  }, []);

  if (isLoading) return <div>Loading...</div>;

  return (
    <Layout>
      <Content style={{ padding: 10 }}>
        <Typography.Title level={1}>Oops!</Typography.Title>

        <Typography.Title level={4}>
          Hemos identificado que necesitas los siguientes permisos
        </Typography.Title>

        <Typography.Paragraph>
          Para poder utilizar esta aplicación, necesitas aprobar los siguientes
          permisos. Si tienes algún problema, por favor contacta a{" "}
          <Typography.Text code copyable>
            felipe@dataroot.cl
          </Typography.Text>
        </Typography.Paragraph>

        <Typography.Paragraph>
          Una vez que hayas aprobado los permisos, por favor recarga la página.
        </Typography.Paragraph>

        <Button type="primary" onClick={onReload} size="small">
          Recargar
        </Button>

        <Typography.Title level={4}>Permisos</Typography.Title>

        <List
          split={false}
          dataSource={permissions}
          renderItem={(permission, index) => {
            const status = statuses?.[index];

            return (
              <List.Item>
                <List.Item.Meta title={permission} />

                <Tag color={status?.state === "granted" ? "green" : "red"}>
                  {status?.state === "granted" ? "Permitido" : "Denegado"}
                </Tag>
              </List.Item>
            );
          }}
        />
      </Content>
    </Layout>
  );
};
